/**
 * THE ONE PLACE A STEAM-DERIVED CARD FIELD IS DECLARED.
 *
 * A Discover tile and a Detail screen render the SAME `Game` row — `detailVM`
 * is literally `{ ...tileVM(dg, ctx), ... }` — so "show this on the card too"
 * has never been a rendering question. It is a question of whether the row was
 * ever filled in. Before this file the answer depended on which fetch a row
 * happened to arrive through, and widening that set meant editing seven places:
 *
 *   1. `worker/src/steam.ts`          the `data_request` flags
 *   2. `worker/src/steam.ts`          the extraction
 *   3. `worker/src/handlers/items.ts` the shard-cache field allow-list
 *   4. `worker/src/handlers/items.ts` the shard-cache KEY VERSION
 *   5. `app/src/lib/types.ts`         `AssetRec` / `SteamFactPatch`
 *   6. `app/src/store/slices/steamFacts.ts`  `itemPatches` + `applyFact`
 *   7. `app/src/repo/steamApi.ts`     `factPatchToRow`, for the write-back
 *
 * Miss #4 and every client serves a record without the new field for a full
 * `ASSET_TTL`, silently. Miss #3 and the field survives the wire and dies in
 * the cache, on the second request only — which is the kind of bug that looks
 * like flakiness. **Both are now derived**, so neither can be forgotten:
 * `ITEM_DATA_REQUEST` computes the flags, `parseItemRecord` computes the
 * allow-list, and `FIELDS_VERSION` fingerprints the enabled set INTO the shard
 * key, so a registry edit invalidates stale shards by construction.
 *
 * Adding a field is one entry below, plus one line on the record interface —
 * and `worker/src/steam.ts` type-asserts that those two agree, so forgetting
 * the second half is a compile error rather than a silent omission.
 *
 * ## What may NOT live here
 *
 * Only facts `IStoreBrowseService/GetItems` can answer about an ARBITRARY
 * appid. That boundary is what makes one registry serve both a catalog row and
 * a live-search row nobody has ever catalogued. Everything else keeps its
 * existing home and is deliberately absent:
 *
 *   `mn` / `mx`     exact player counts — `game_players`, resolved from Steam
 *                   categories / PCGW / IGDB (migrations 2906-2908). GetItems
 *                   reports the CATEGORY axes (`pcats` below) and never a count.
 *   `rt` / `pk`     review tier and all-time peak — curated editorial (D2).
 *   `tags`          the crew's own word list. Steam's vocabulary is `stags`,
 *                   and mixing the two silently breaks every tag filter
 *                   (`repo/steamApi.ts#toBrowseGame`).
 *   `ccu` / `popScore` / `owned` / `why`
 *                   the sweep's samples and `discover_page`'s server-side
 *                   joins. None is a property of a store item.
 *
 * ## Shared by the app and the Worker, and by nothing else
 *
 * `supabase/functions/_shared/poolmap.ts` reads the same upstream payload for
 * the nightly sweep and CANNOT import this: it is Deno, and `npm:` specifiers
 * do not resolve in the Worker or Vite builds. That pair stays mirrored by hand
 * exactly as it already is — see `poolmap.ts#dollarsFromCents` on why two
 * copies of a price rule that disagree is this repo's most reliable bug. The
 * app/Worker boundary has no such excuse: both are npm workspaces bundled from
 * TypeScript source, so they share this file rather than a convention.
 *
 * The module is import-free and side-effect-free on purpose — that is what lets
 * a Cloudflare Worker, a Vite bundle and a Vitest run all take it unchanged.
 */

// ---------------------------------------------------------------------------
// values
// ---------------------------------------------------------------------------

/**
 * Everything a card field is allowed to be on the wire.
 *
 * The array arm is MUTABLE (`number[]`, not `readonly number[]`) because every
 * reader and validator here constructs a fresh array — nothing is ever aliased
 * out of a caller's payload — and `Game.pcats` on the client is a plain
 * `number[]`. A readonly type would buy no safety and would force a defensive
 * copy at every assignment.
 */
export type CardValue = string | number | boolean | number[];

/**
 * Which of a record's two clocks a field expires on (`handlers/items.ts`).
 *
 *   `art`       ~immutable, `ASSET_TTL` (24 h). Art, the ribbon flags, and
 *               anything that only moves when a game SHIPS or is re-listed.
 *   `volatile`  moves on its own, `VOLATILE_TTL` (30 min). Price and rating.
 *
 * The split is not cosmetic: an upstream failure keeps every `art` field it
 * already holds and re-asks only for the `volatile` half, which is what stops
 * one Steam hiccup from blanking a screenful of warm tiles.
 */
export type Clock = 'art' | 'volatile';

/**
 * How a field is lifted off one `store_items` entry.
 *
 *   a reader fn  the field stands alone — `read` gets the raw item and returns
 *                the value, or `undefined` for "not answered".
 *   `'flags'`    decided by `worker/src/steam.ts#itemFlags`, together.
 *   `'volatile'` decided by `worker/src/steam.ts#itemVolatile`, together.
 *   `'assets'`   the art pair, built in `mapStoreItems` from `asset_url_format`.
 *
 * THE GROUPS EXIST BECAUSE THOSE FIELDS ARE NOT INDEPENDENT, and flattening
 * them into per-field readers would quietly discard the reasoning in those two
 * functions. `up`/`ea` are suppressed together on a delisted item because the
 * proto3 "absence means false" inference stops holding there; `p`/`o` are
 * decided by an ORDERED cascade over `unlisted` → `is_free` →
 * `best_purchase_option` in which no single field can be read on its own. A
 * registry that could express "read `is_free` into `p`" would be a registry
 * that lets the next person mis-price every free-to-play game.
 */
export type Source =
  | { readonly kind: 'read'; readonly read: (item: ItemLike) => CardValue | undefined }
  | { readonly kind: 'group'; readonly group: 'flags' | 'volatile' | 'assets' };

/** One `store_items` entry, before anything has vouched for its shape. */
export type ItemLike = Record<string, unknown>;

// ---------------------------------------------------------------------------
// readers for the standalone fields
// ---------------------------------------------------------------------------

const isRecord = (v: unknown): v is ItemLike => typeof v === 'object' && v !== null && !Array.isArray(v);

/** A non-empty trimmed string, or `undefined`. Never `''` — see `parseText`. */
const text = (v: unknown): string | undefined => {
  if (typeof v !== 'string') return undefined;
  const t = v.trim();
  return t === '' ? undefined : t;
};

/**
 * `release.steam_release_date` (Unix seconds, UTC) → `'YYYY-MM'`.
 *
 * MIRRORS `poolmap.ts#ymFromEpoch`, which has written `steam_facts.release_ym`
 * from this exact field since migration 2900 — so the shape is not a new
 * agreement, it is the one the column already holds and `qd_recency` already
 * parses. `0` is Steam's "unknown", not an epoch, and is rejected as such.
 *
 * This field rode the items payload UNASKED from the day `include_release` went
 * on (it is in the same `release` sub-message as `is_coming_soon`), and
 * `itemFlags` read its two siblings and stepped over it. A Discover tile
 * therefore showed an em dash for the release date of a game whose date the
 * Worker had already been handed, and the only thing that ever filled it in was
 * opening the Detail screen — because `mapAppDetails` parses a DIFFERENT
 * endpoint's `release_date.date` string. That is the whole reason this registry
 * exists: the payload was never the constraint, the extraction was.
 */
const releaseYm = (item: ItemLike): string | undefined => {
  const release = isRecord(item['release']) ? item['release'] : null;
  if (release === null) return undefined;
  const raw = release['steam_release_date'];
  const secs = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(secs) || secs <= 0) return undefined;
  const d = new Date(secs * 1000);
  const y = d.getUTCFullYear();
  if (y < 1970 || y > 2100) return undefined;
  return `${y}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};

/**
 * `categories.supported_player_categoryids` — the co-op / multiplayer axes.
 *
 * `mapBrowseRows` has read this exact path since browse shipped, off the same
 * endpoint, three hundred lines from `itemVolatile`. The items route never
 * looked, so a search tile could say CO-OP and the identical appid on the feed
 * could say nothing, depending only on which fetch put the row in the store.
 *
 * DISPLAY ONLY. `playersTxt` reads it when `mx` is the 0 sentinel; `knownOf`
 * and `passFlt` keep reading the raw `mn`/`mx`, so nothing here can narrow a
 * filter (DISCOVERY decision 5: unknown never excludes).
 */
const playerCats = (item: ItemLike): number[] | undefined => {
  const cats = isRecord(item['categories']) ? item['categories'] : null;
  const raw = cats?.['supported_player_categoryids'];
  if (!Array.isArray(raw)) return undefined;
  const out = raw
    .map((x) => (typeof x === 'number' ? x : Number(x)))
    .filter((n) => Number.isSafeInteger(n) && n > 0);
  // ABSENT, never `[]`: an item whose payload carried no categories block must
  // not be recorded as a game that HAS no multiplayer (`lib/types.ts#pcats`).
  return out.length ? out : undefined;
};

/** `basic_info.developers[0].name`. */
const developer = (item: ItemLike): string | undefined => {
  const basic = isRecord(item['basic_info']) ? item['basic_info'] : null;
  const devs = basic && Array.isArray(basic['developers']) ? basic['developers'] : [];
  const first = devs[0];
  return isRecord(first) ? text(first['name']) : undefined;
};

/** `basic_info.short_description` — Detail's `descTxt`. */
const shortDesc = (item: ItemLike): string | undefined => {
  const basic = isRecord(item['basic_info']) ? item['basic_info'] : null;
  return basic ? text(basic['short_description']) : undefined;
};

/** `platforms` → the `Game.plat` letter set. Only P is derivable from Steam. */
const platforms = (item: ItemLike): string | undefined => {
  const p = isRecord(item['platforms']) ? item['platforms'] : null;
  if (p === null) return undefined;
  const windows = p['windows'] === true;
  const mac = p['mac'] === true;
  const linux = p['steamos_linux'] === true || p['linux'] === true;
  // A Steam store item is a PC product by definition; the console letters in
  // `Game.plat` are editorial and no GetItems response can speak to them.
  return windows || mac || linux ? 'P' : undefined;
};

/** `tagids`, weight-ordered, capped at the display ceiling. */
const steamTagIds = (item: ItemLike): number[] | undefined => {
  const raw = item['tagids'];
  if (!Array.isArray(raw)) return undefined;
  const out = raw
    .map((x) => (typeof x === 'number' ? x : Number(x)))
    .filter((n) => Number.isSafeInteger(n) && n > 0)
    .slice(0, 20);
  return out.length ? out : undefined;
};

// ---------------------------------------------------------------------------
// validators — the shard-cache and wire allow-list
// ---------------------------------------------------------------------------

const parseText = (v: unknown): string | undefined => (typeof v === 'string' && v !== '' ? v : undefined);
const parseBool = (v: unknown): boolean | undefined => (typeof v === 'boolean' ? v : undefined);
const parseAppid = (v: unknown): number | undefined =>
  typeof v === 'number' && Number.isSafeInteger(v) && v > 0 ? v : undefined;
/** Dollars. `0` is a real value — free, or "priced but not discounted". */
const parseDollars = (v: unknown): number | undefined =>
  typeof v === 'number' && Number.isFinite(v) && v >= 0 ? v : undefined;
/**
 * 1..100. `0` is REJECTED rather than stored: upstream it means "no reviews
 * yet", and `Game.rp` already uses 0 as the "nobody told us" sentinel, so
 * writing it would turn a silence into a claim that a game is rated 0%.
 */
const parsePct = (v: unknown): number | undefined =>
  typeof v === 'number' && Number.isInteger(v) && v >= 1 && v <= 100 ? v : undefined;
/**
 * Steam's own review rung, `reviews.summary_filtered.review_score`, 1..9.
 *
 * `0` is rejected for the same reason `parsePct` rejects it, and Steam is
 * explicit about the case: it emits `review_score: 0` WITH
 * `review_score_label: "No user reviews"` rather than omitting the field, so
 * absent and zero are distinguishable on the wire and neither is a rung.
 *
 * THIS IS THE FIELD THAT KNOWS ABOUT REVIEW COUNT. Steam's ladder folds the
 * count into the rung — Overwhelmingly Positive is 95%+ AND 500+ reviews, Very
 * Positive is 80%+ AND 50+, and a 96% game with twelve reviews is merely
 * `Positive` (7) — which a percentage alone cannot express. Storing the rung is
 * how the app stops claiming OVERWHELMING over a dozen reviews without carrying
 * a review count of its own (2026-08-27, Marcus: match Steam's shape).
 */
const parseScore = (v: unknown): number | undefined =>
  typeof v === 'number' && Number.isInteger(v) && v >= 1 && v <= 9 ? v : undefined;
/** `'YYYY-MM'`, the shape `steam_facts.release_ym` and `fmtD` both expect. */
const parseYm = (v: unknown): string | undefined =>
  typeof v === 'string' && /^\d{4}-(0[1-9]|1[0-2])$/.test(v) ? v : undefined;
/** `'YYYY-MM-DD'`, the shape `steam_facts.release_date` and `fmtDFull` expect. */
const parseYmd = (v: unknown): string | undefined =>
  typeof v === 'string' && /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/.test(v) ? v : undefined;
/**
 * The items path has NO honest source for the release DAY, and this says so in
 * the only place the registry lets it be said.
 *
 * The obvious candidate is `release.steam_release_date`, which every other
 * `release` field reads from — and Steam sets that to a PLACEHOLDER for
 * coming-soon titles, end of quarter or end of year. Deriving a day from it
 * would mint a confident `2026-12-31` for a game whose own display string reads
 * `Q4 2026`, and that fabricated day would then WIN the client's
 * `rdFull || fmtD(g)` fallback and replace an honest `TBA`.
 *
 * So this field is APPDETAILS-ONLY: `mapAppDetails` parses it out of Steam's
 * own words, where both patterns require a literal day and prose therefore
 * yields nothing. The entry exists purely so `column` is registered and
 * `factPatchToRow` can write the value back; it never reads from an item.
 * `parseYmd` above is the guard that would apply if anyone ever wires one.
 */
const readNoDayFromItems = (): undefined => undefined;
const parseIds = (v: unknown): number[] | undefined => {
  if (!Array.isArray(v)) return undefined;
  const out = v.filter((n): n is number => typeof n === 'number' && Number.isSafeInteger(n) && n > 0);
  return out.length ? out : undefined;
};

// ---------------------------------------------------------------------------
// the registry
// ---------------------------------------------------------------------------

export interface CardField {
  /** The wire key, and the key on `SteamFactPatch` / `AssetRec`. */
  readonly key: string;
  /**
   * `false` keeps the entry — its reader, validator and cost note — while
   * spending nothing. Flip it to `true` to ship the field; nothing else moves,
   * and `FIELDS_VERSION` changes with it so no client is served a stale record
   * that predates the decision.
   */
  readonly enabled: boolean;
  /**
   * The `data_request` flag this field needs, or `null` when it rides the
   * payload unconditionally. `ITEM_DATA_REQUEST` unions the flags of every
   * ENABLED field, so a disabled entry costs no bytes.
   */
  readonly flag: string | null;
  /** `true` for a boolean flag; a number for `include_tag_count`. */
  readonly flagValue: number | true;
  readonly clock: Clock;
  /**
   * The `steam_facts` column `upsert_steam_facts` accepts for this field, or
   * `null` when the field is display-only and MUST NOT be written back.
   *
   * `null` is the answer for three different reasons and they are worth
   * keeping apart:
   *   `rp`            migration 2909 deliberately REMOVED `review_pct` from
   *                   the client write path. Pool-only now; do not re-open it.
   *   `dev` / `ds`    curated columns (D2). Their Steam-derived twins live in
   *                   `game_features`, which only the pool's `op=features`
   *                   writes.
   *   `pcats`/`stags`/`plat`
   *                   no column exists, by design — see the header.
   */
  readonly column: string | null;
  readonly source: Source;
  /** Validates a value off the wire or out of the shard cache. */
  readonly parse: (v: unknown) => CardValue | undefined;
}

/**
 * ORDER IS THE WIRE ORDER and nothing depends on it, but keep the art pair
 * first and the newcomers last so a diff reads as an addition.
 *
 * On the two entries that are `enabled: false` — the flags are measured, not
 * guessed. Against 50 live appids on 2026-08-12, over the baseline payload:
 *
 *   include_basic_info   +24%   dev AND the short description
 *   include_platforms    +10%   one letter this app can already infer
 *   include_tag_count:20 +38%   the biggest single cost in the payload
 *
 * Those are UPSTREAM bytes on a cache MISS, Worker↔Steam — the client is served
 * extracted records either way, so none of it reaches a phone. `basic_info`
 * earns its 24% because it fills a field the card renders today. The other two
 * buy fields only the Detail screen shows, and `stags` costs more than
 * everything else combined, so they wait until something renders them.
 */
export const CARD_FIELDS = [
  {
    key: 'lib',
    enabled: true,
    flag: 'include_assets',
    flagValue: true,
    clock: 'art',
    column: null,
    source: { kind: 'group', group: 'assets' },
    parse: parseText,
  },
  {
    key: 'hdr',
    enabled: true,
    flag: 'include_assets',
    flagValue: true,
    clock: 'art',
    column: null,
    source: { kind: 'group', group: 'assets' },
    parse: parseText,
  },
  {
    key: 'up',
    enabled: true,
    flag: 'include_release',
    flagValue: true,
    clock: 'art',
    column: 'upcoming',
    source: { kind: 'group', group: 'flags' },
    parse: parseBool,
  },
  {
    key: 'ea',
    enabled: true,
    flag: 'include_release',
    flagValue: true,
    clock: 'art',
    column: 'early_access',
    source: { kind: 'group', group: 'flags' },
    parse: parseBool,
  },
  {
    key: 'demo',
    enabled: true,
    flag: null,
    flagValue: true,
    clock: 'art',
    column: 'has_demo',
    source: { kind: 'group', group: 'flags' },
    parse: parseBool,
  },
  {
    key: 'dAid',
    enabled: true,
    flag: null,
    flagValue: true,
    clock: 'art',
    column: 'demo_appid',
    source: { kind: 'group', group: 'flags' },
    parse: parseAppid,
  },
  {
    key: 'p',
    enabled: true,
    flag: 'include_purchase_options',
    flagValue: true,
    clock: 'volatile',
    column: 'price',
    source: { kind: 'group', group: 'volatile' },
    parse: parseDollars,
  },
  {
    key: 'o',
    enabled: true,
    flag: 'include_purchase_options',
    flagValue: true,
    clock: 'volatile',
    column: 'old_price',
    source: { kind: 'group', group: 'volatile' },
    parse: parseDollars,
  },
  {
    key: 'rp',
    enabled: true,
    flag: 'include_reviews',
    flagValue: true,
    clock: 'volatile',
    column: null,
    source: { kind: 'group', group: 'volatile' },
    parse: parsePct,
  },
  {
    // Free beside `rp`: same `include_reviews` block, same sub-message, no
    // extra upstream bytes. `column: null` for the same reason `rp` has it —
    // migration 2909 closed `upsert_steam_facts` to review writes, and the rung
    // reaches the database only through the service-role pool job.
    key: 'rs',
    enabled: true,
    flag: 'include_reviews',
    flagValue: true,
    clock: 'volatile',
    column: null,
    source: { kind: 'group', group: 'volatile' },
    parse: parseScore,
  },
  {
    key: 'rd',
    enabled: true,
    flag: 'include_release',
    flagValue: true,
    // Art's clock, beside `up`/`ea` from the same `release` sub-message: a
    // release date moves when a game SHIPS, which is the event those two flags
    // already track, and never on a 30-minute scale.
    clock: 'art',
    column: 'release_ym',
    source: { kind: 'read', read: releaseYm },
    parse: parseYm,
  },
  {
    // The DAY half of `rd`. Same sub-message and therefore the same flag, so it
    // costs no extra upstream bytes — `ITEM_DATA_REQUEST` unions the flags.
    key: 'rdFull',
    enabled: true,
    flag: 'include_release',
    flagValue: true,
    clock: 'art',
    column: 'release_date',
    // Deliberately never sourced from an item — see `readNoDayFromItems`.
    source: { kind: 'read', read: readNoDayFromItems },
    parse: parseYmd,
  },
  {
    key: 'pcats',
    enabled: true,
    flag: null,
    flagValue: true,
    clock: 'art',
    column: null,
    source: { kind: 'read', read: playerCats },
    parse: parseIds,
  },
  {
    key: 'dev',
    enabled: true,
    flag: 'include_basic_info',
    flagValue: true,
    clock: 'art',
    column: null,
    source: { kind: 'read', read: developer },
    parse: parseText,
  },
  {
    key: 'ds',
    enabled: true,
    flag: 'include_basic_info',
    flagValue: true,
    clock: 'art',
    column: null,
    source: { kind: 'read', read: shortDesc },
    parse: parseText,
  },
  {
    key: 'stags',
    enabled: false,
    flag: 'include_tag_count',
    flagValue: 20,
    clock: 'art',
    column: null,
    source: { kind: 'read', read: steamTagIds },
    parse: parseIds,
  },
  {
    key: 'plat',
    enabled: false,
    flag: 'include_platforms',
    flagValue: true,
    clock: 'art',
    column: null,
    source: { kind: 'read', read: platforms },
    parse: parseText,
  },
] as const satisfies readonly CardField[];

/**
 * Every wire key the registry knows, enabled or not.
 *
 * `worker/src/steam.ts` and `app/src/lib/types.ts` both assert their record
 * interface against this union in BOTH directions, so a registry entry with no
 * field on the interface, and a field on the interface with no registry entry,
 * are each a compile error. That is the half of "adding a field is one entry"
 * that a runtime table cannot enforce on its own.
 */
export type CardFieldKey = (typeof CARD_FIELDS)[number]['key'];

// ---------------------------------------------------------------------------
// derived — nothing below is hand-maintained
// ---------------------------------------------------------------------------

/** The registry at its widened type; the const tuple exists for `CardFieldKey`. */
const ALL: readonly CardField[] = CARD_FIELDS;
const ENABLED: readonly CardField[] = ALL.filter((f) => f.enabled);

/** The enabled fields whose value is lifted by their own reader. */
type StandaloneField = CardField & {
  source: { kind: 'read'; read: (item: ItemLike) => CardValue | undefined };
};
export const STANDALONE_FIELDS: readonly StandaloneField[] = ENABLED.filter(
  (f): f is StandaloneField => f.source.kind === 'read',
);

/**
 * The enabled keys that describe the GAME rather than its art.
 *
 * `lib`/`hdr` ride the same record but land in the asset map, not in
 * `steamFacts.facts` — a capsule URL is not a fact about a game the way a price
 * or a release month is, and conflating the two is exactly the mistake that
 * disabled the whole revalidation ladder before Phase 4 (`revalidateSurface`
 * has the full account).
 */
export const PATCH_KEYS: readonly string[] = ENABLED.filter(
  (f) => !(f.source.kind === 'group' && f.source.group === 'assets'),
).map((f) => f.key);

/** wire key → `steam_facts` column, for every field the write-back may carry. */
export const WRITEBACK_COLUMNS: Readonly<Record<string, string>> = Object.freeze(
  Object.fromEntries(ENABLED.filter((f) => f.column !== null).map((f) => [f.key, f.column as string])),
);

/** The keys that expire on each clock, for `handlers/items.ts#reconcile`. */
export const VOLATILE_KEYS: readonly string[] = ENABLED.filter((f) => f.clock === 'volatile').map((f) => f.key);

/**
 * The `data_request` object, unioned from the enabled fields' flags.
 *
 * `steam_realm: 1` and `include_assets: true` are load-bearing elsewhere — drop
 * either and `store_items` comes back empty — and the `lib`/`hdr` entries above
 * are what keep `include_assets` in this object, so the invariant is now held
 * by the registry rather than by a comment asking nobody to touch it.
 */
export const ITEM_DATA_REQUEST: Readonly<Record<string, number | true>> = Object.freeze(
  ENABLED.reduce<Record<string, number | true>>((acc, f) => {
    if (f.flag !== null) acc[f.flag] = f.flagValue;
    return acc;
  }, {}),
);

/**
 * A fingerprint of the enabled field set, stamped into the shard-cache key.
 *
 * THIS IS THE BUG THAT CANNOT HAPPEN ANY MORE. The key used to carry a
 * hand-written `v3`, with a comment explaining that a `v2` record has no `v`
 * stamp and no `p`/`o`/`rp` so reading one would leave an appid serving a
 * flagless record for a full 24 h. That comment was correct and it was also an
 * instruction to remember something at exactly the moment you are thinking
 * about something else. The version is now a function of the thing it versions:
 * enable a field, disable one, or change a validator's key, and every shard
 * written under the old set becomes unreachable rather than wrong.
 *
 * FNV-1a over the enabled keys and their flags — order-independent by sorting
 * first, so reordering the registry does NOT needlessly dump the cache.
 */
export const FIELDS_VERSION: string = (() => {
  const seed = ENABLED.map((f) => `${f.key}:${f.flag ?? '-'}:${String(f.flagValue)}:${f.clock}`)
    .sort()
    .join('|');
  let h = 0x811c9dc5;
  for (let i = 0; i < seed.length; i += 1) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return 'f' + h.toString(36);
})();

/**
 * Validate one record — off the wire on the client, out of the shard cache in
 * the Worker. Unknown keys are DROPPED and every known one must clear its own
 * validator, so a shard written by an older deploy, a truncated write, or a
 * future field can only ever produce a thinner record, never a wrong one.
 *
 * Returns a plain object rather than a typed record because its two callers
 * assert different interfaces over it; both are checked against the registry at
 * compile time where they do.
 */
export function parseItemRecord(raw: unknown): Record<string, CardValue> | null {
  if (!isRecord(raw)) return null;
  const out: Record<string, CardValue> = {};
  for (const f of ENABLED) {
    const v = f.parse(raw[f.key]);
    if (v !== undefined) out[f.key] = v;
  }
  return out;
}

/**
 * Every standalone field this item answers. The group readers
 * (`itemFlags` / `itemVolatile` / the asset pair) stay in
 * `worker/src/steam.ts`, which is the only place that has the argument for
 * why their fields are decided together.
 */
export function readStandalone(item: ItemLike): Record<string, CardValue> {
  const out: Record<string, CardValue> = {};
  for (const f of STANDALONE_FIELDS) {
    const v = f.source.read(item);
    if (v !== undefined) out[f.key] = v;
  }
  return out;
}
